<template>
  <DefaultLayout>
  <div class="image-detail">
<header class="detail-header">
  <span class="back-icon"
      aria-label="Go back to previous page" role="button" 
      tabindex="0" @click="goBack" @keyup.enter.prevent="goBack"><</span>
  <h1>Image Analysis</h1>
</header>

    <main id="main-content"class="detail-content" tabindex="-1" aria-label="Main content of image analysis page">



      <!-- 좌측: 이미지 영역 -->
<div class="image-section">
  <div class="image-card">

    <!-- 이미지 -->
    <div class="image-wrapper">
      <img :src="imageUrl" :alt="imageName" class="main-image" />
    </div>

    <!-- Image Information -->
    <div class="imagedata-details">
      <h4>📋 Image Information</h4>

      <div class="info-grid">
        <div class="info-item">
          <span class="label">ID</span>
          <span class="value">{{ imageData.id }}</span>
        </div>

        <div class="info-item">
          <span class="label">rID</span>
          <span class="value">{{ imageData.rId }}</span>
        </div>

        <div class="info-item">
          <span class="label">Type</span>
          <span class="value">{{ imageData.objectType }}</span>
        </div>

        <div class="info-item">
          <span class="label">Name</span>
          <span class="value">{{ imageData.name }}</span>
        </div>
      </div>
    </div>

  </div>
</div>





      <!-- 우측: 분석 및 대화 영역 -->
      <div class="analysis-section">
        <!-- 이미지 분석 결과 -->
    
        <!--
          <h3>📊 이미지 분석 결과</h3>
          -->
          
       
          
<div class="tts-rate-wrapper">
  <div class="tts-rate-header">
    <h4 class="tts-rate-title">🕪 Voice Speed</h4>

    <!-- ✅ 현재 속도 배지 -->
    <span class="current-rate-badge">
      {{ rate }}x
    </span>
  </div>

  <div class="tts-rate-container">
    <div class="rate-buttons-wrapper">
      <button 
        v-for="num in [0.5,1,1.25,1.5,2]" 
        :key="num"
        class="rate-btn"
        :class="{ active: rate === num }"
        :aria-pressed="rate === num"
        :aria-label="`Set voice speed to ${num}x`"
        @click="selectRate(num)"
      >
        {{ num }}x
      </button>
    </div>
  </div>
</div>



<section 
  v-if="imageData.isAnalyzed && imageData.analyzed" class="ai-analysis"
  aria-labelledby="ai-analysis">
  
  <!-- 헤더 -->
  <div class="ai-analysis-header">
    <h4>✦ AI Analysis Result</h4>
    <button @click="playAnalysisTTS" class="tts-btn">
      🕪 Read Aloud
    </button>
  </div>

  <!-- 바디 -->
  <div class="ai-analysis-body">
    <button 
        @click="toggleJsonExpanded" 
        class="json-toggle-btn"
    > {{ jsonExpanded ? 'Collapse' : 'View All' }}
    </button>
    <pre
      class="analysis-json"
      tabindex="-1"
      :class="{ expanded: jsonExpanded }"
    >
      <span class="notranslate">{{ formatAnalysisData(imageData.analyzed) }}</span>
    </pre>
  </div>

</section>






        <!-- 대화 창 -->
<!-- Chat Panel -->
<section class="chat-panel" aria-label="Chat">

  <!-- STT Indicator Bar -->
  <div
    class="stt-indicator-bar"
    :class="{ listening: isListening }"
    aria-live="polite" role="status"
  >
    <span v-if="isListening">🎤 Listening… (Release Ctrl + Shift)</span>
    <span v-else>● Hold Ctrl + Shift to start voice recognition</span>
  </div>

  <!-- Error -->
  <div v-if="sttError" class="error">
    {{ sttError }}
  </div>

  <!-- Messages -->
  <div class="chat-messages" ref="chatMessages"
       role="log" aria-polite="polite" aria-relevant="additions">
    <div
      v-for="message in chatMessages"
      :key="message.id"
      :class="['message', message.type]"
    >
      <div class="message-content">
        <span class="notranslate">{{ message.content }}</span>
        <button
          v-if="message.type === 'assistant'"
          class="tts-btn"
          @click="playChatTTS(message)"
        >
          🔊
        </button>
      </div>
      <div class="message-time">
        {{ formatTime(message.timestamp) }}
      </div>
    </div>
  </div>

  <!-- Input -->
  <div class="chat-input-area">
    <div class="input-container">
      <input
        v-model="currentMessage"
        @keyup.enter="sendMessage"
        placeholder="Ask a question about the image..."
        class="chat-input"
        :disabled="chatLoading"
      />
      <button
        class="send-btn"
        @click="sendMessage"
        :disabled="!currentMessage.trim() || chatLoading"
      >
        {{ chatLoading ? '...' : 'Send' }}
      </button>
    </div>
  </div>

</section>




      </div>
    </main>
  </div>

</DefaultLayout>
</template>

<script>

// import { generateImageCaption, generateVLMChatResponse, speakText } from '@/js/VLM.js';
import DefaultLayout from "@/layouts/DefaultLayout.vue";
import { speakText, stopSpeaking, skipSentence, replayPrevious, togglePlayPause} from '@/js/VLM.js';
import ApiService from '@/services/ApiService';
import { DotpadList } from "@/js/dot/DotpadList.js";
import { dotpadState, dotpadTTS, dotpadwebCam, dotpadStartListening, dotpadStopListening, dotpadButton3Hold } from "@/js/dot/Dotpad.js";
// import WhisperService from '@/services/WhisperService.js';
import { connectWS, onWS, sendJSON, isOpen, disconnectWS } from '@/socket/socket.js';
import thinkingSndUrl from '@/assets/sound/thinking.mp3';
import doneSndUrl     from '@/assets/sound/done.mp3';
import { mapGetters } from 'vuex'; 


let labels = [];
let assistant_result = "";

// 안전 볼륨 세터 (어디서든 재사용)
function setSafeVolume(mediaEl, v) {
  try {
    const n = Number(v);
    const clamped = Number.isFinite(n) ? Math.max(0, Math.min(1, n)) : 0;
    if (mediaEl) mediaEl.volume = clamped;
  } catch (e) {
    // 무시 (엘리먼트가 아직 없음 등)
  }
}



export default {
  name: 'ImageDetail',
  components: {
    DefaultLayout,
  },
  props: { id: { type: String, required: true } },

  data() {
    return {
      braillePages: [],
    currentBraillePageIndex: 0,
      streamReady: false,
      isAlive: true,
      // Image meta
      imageData: {
        id: '', rId: '', objectType: '', file: '', name: '',
        size: null, data: null, isUploaded: false, isAnalyzed: false,
        analyzed: null, ctrlDown: false, shiftDown: false
      },

      // UI state
      analysisLoading: false,
      chatLoading: false,
      currentMessage: '',
      F3_currentMessage: '',
      F3_sendTipToApi: [],
      chatMessages: [],
      detectedObjects: [],
      dominantColors: [],
      imageCharacteristics: '',
      suggestedQuestions: [
        '이 이미지에서 무엇을 볼 수 있나요?',
        '이미지의 주요 색상은 무엇인가요?',
        '이 이미지가 STEM 교육에 어떻게 활용될 수 있나요?',
        '이미지의 구성 요소를 설명해주세요.'
      ],
      messageIdCounter: 0,
      jsonExpanded: false,

      // STT
      isListening: false,
      // streamController: null,
      transcription: '',
      // realTimeResults: [],
      sttError: null,
      // isInitializing: false,
      // Web Speech API
      recognition: null,
      sttMode: null,   // 'chat' or 'f3'
      _speechStarting: false,  // start() 중복 호출 막는 플래그

      // TTS
      ttsEnabled: true,
      rate: 1,
      ours: true,

      // Timers
      elapsed0Ms: 0, timer0Running: false, timer0Interval: null, started0At: null,
      elapsed1Ms: 0, timer1Running: false, timer1Interval: null, started1At: null,
      elapsed2Ms: 0, timer2Running: false, timer2Interval: null, started2At: null,

      // Dotpad
      legend: [],
      chart: [],
      currentDotpadState: "1",
      currentDotpadTTS: "",
      currentDotpadwebCam: false,
      currentDotpadStartListening: false,
      currentDotpadStopListening: false,
      currentDotpadButton3Hold: false,

      // WS
      wsConnected: true,
      wsStatus: '',
      // wsFrameSrc: '',
      // wsServerFrame: 0,
      // wsAruco: false,
      // wsHand: false,
      wsSaveStatus: '대기 중',
      wsSavedRoiSrc: '',
      wsSavedRoiSize: null, // {width,height}
      wsFps: 0,

      // 스냅샷용 새 필드
      debugImage: null,
      roiPreview: null,
      lastGrid: null,
      lastTipRoi: null,
      lastPayloadText: '',
      autoCalibrated: false,

      pendingTip: null,      // F3 한 번에 사용할 tip 후보
      pendingQuery: '',      // F3 한 번에 사용할 음성 질의 텍스트

      // API flags
      apiSending: false,
      
      apiResult: '',
      apiError: '',
      wsLastRoiSig: '',
      wsLastTipSig: '',
      apiSendingTip: false,
      apiErrorTip: '',
      apiResultTip: '',

      // Tip/ROI pairing (윈도우 기반)
      lastSaveRoiAt: 0, // save_roi 요청 시각(ms)
      roiReadyAt: 0,    // ROI 이미지 준비 시각(ms)
      pendingTip: null, // ROI 준비 전에 도착한 tip 보관

      // Devices
      devices: [],
      selectedDeviceId: "",
      stream: null,
      streamReady: false,

      // Audio
      thinkingAudio: null,
      doneAudio: null,
      _audioUnlocked: false,
      _fadeRAF: null,
      _fadeToken: 0,

      // Output staging flag (printPixels 완료 전까지 thinking 유지)
      outputStaging: false,

      _keyOpts: { capture: true, passive: false },
    };
  },

  created() {
    this.loadImageData();
    this.loadOursBaselineState();
    this.loadRateState();

    const v = this.$route.query.ours_or_baseline;
    if (v !== undefined) {
      this.ours = (v === '1' || v === 'true' || v === true);
      this.$nextTick(() => (this.ours ? this.selectOurs() : this.selectBaseline()));
    }
  },

  async mounted() {
    this.streamReady = false;

  // watcher 등록
  const watcher = this.$watch('streamReady', (val) => {
    if (val && !this.autoCalibrated) {
      this.autoCalibrated = true;
      this.onClickCalibrate();
    }
  });

  // 장치 목록 로드
  await this.refreshDevices();

  // 🔥 autoConnectCamera 사용
  await this.autoConnectCamera();


    this.printPixels();
    window.addEventListener('dotpad-state-changed', this.onDotpadStateChanged, { passive: true });
    window.addEventListener('dotpad-TTS-changed', this.onDotpadTTSChanged, { passive: false });
    window.addEventListener('dotpad-webCam-changed', this.onDotpadwebCamChanged, { passive: false });
    window.addEventListener('dotpad-start-listening', this.onDotpadStartListening, { passive: false });
    window.addEventListener('dotpad-stop-listening', this.onDotpadStopListening, { passive: false });
    this.currentDotpadState = dotpadState;
    this.currentDotpadTTS = dotpadTTS;
    this.currentDotpadwebCam = dotpadwebCam;
    this.currentDotpadStartListening = dotpadStartListening;
    this.currentDotpadStopListening = dotpadStopListening;

    // Audio init
    this.thinkingAudio = new Audio(thinkingSndUrl);
    this.thinkingAudio.loop = true;

    this.thinkingAudio.volume = 0.0;


    this.doneAudio = new Audio(doneSndUrl);
    setSafeVolume(this.doneAudio, 0.18);

    // Unlock audio on user gesture
    const unlock = () => {
      if (this._audioUnlocked) return;
      this._audioUnlocked = true;
      this.thinkingAudio.muted = false;
      this.doneAudio.muted = false;
      this.doneAudio.play().then(() => this.doneAudio.pause()).catch(() => {});
      if (this.isThinking) this.startThinkingSound();
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
    window.addEventListener('pointerdown', unlock, { once: true, passive: true });
    window.addEventListener('keydown', unlock, { once: true });

    // Start analysis
    this.processAnalysisData();
    if (this.isThinking) this.startThinkingSound();

    // Devices
    if (!navigator?.mediaDevices?.getUserMedia) {
      console.warn("이 브라우저는 카메라 API를 지원하지 않습니다.");
    } else {
      await this.refreshDevices();
      this._deviceChangeHandler = this.refreshDevices;
      navigator.mediaDevices.addEventListener("devicechange", this._deviceChangeHandler);
    }

    // Shortcuts
    window.addEventListener('keydown', this.handleCtrlShiftDown);
    window.addEventListener('keyup', this.handleCtrlShiftUp);
    window.addEventListener('keydown', this.handleTTSShortcuts, this._keyOpts);
    window.__imageDetailHotkeysBound__ = true;

    // WS
    onWS('open', this.handleWSOpen);
    onWS('close', this.handleWSClose);
    onWS('message', this.handleWSMessage);  // 스냅샷으로 변경
  },

  beforeUnmount() {
    // All audio/TTS off
    this.isAlive = false;
    this.cleanupAudioTTS();

    window.removeEventListener('dotpad-state-changed', this.onDotpadStateChanged);
    window.removeEventListener('dotpad-TTS-changed', this.onDotpadTTSChanged);
    window.removeEventListener('dotpad-webCam-changed', this.onDotpadwebCamChanged);
    window.removeEventListener('dotpad-start-listening', this.onDotpadStartListening);
    window.removeEventListener('dotpad-stop-listening', this.onDotpadStopListening);

    try {
      if (this._deviceChangeHandler) {
        navigator.mediaDevices.removeEventListener("devicechange", this._deviceChangeHandler);
      }
    } catch {}

    this.stopStream();
    window.removeEventListener('keydown', this.handleCtrlShiftDown);
    window.removeEventListener('keyup', this.handleCtrlShiftUp);
    window.removeEventListener('keydown', this.handleTTSShortcuts, this._keyOpts);
    window.__imageDetailHotkeysBound__ = false;

    if (this.timer0Interval) clearInterval(this.timer0Interval);
    if (this.timer1Interval) clearInterval(this.timer1Interval);
    if (this.timer2Interval) clearInterval(this.timer2Interval);

    disconnectWS();
  },

  // 페이지 이탈 시에도 정지
  beforeRouteLeave(to, from, next) {
    this.cleanupAudioTTS();
    next();
  },

  computed: {
    ...mapGetters(['speechLangCode', 'currentLanguage']),
    imageUrl() { return this.imageData.file || '' },
    imageName() { return this.imageData.name || '이미지' },
    imageSize() { return this.imageData.size || 0 },

    formattedTime0() {
      const s = Math.floor(this.elapsed0Ms / 1000);
      return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
    },
    formattedTime1() {
      const s = Math.floor(this.elapsed1Ms / 1000);
      return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
    },
    formattedTime2() {
      const s = Math.floor(this.elapsed2Ms / 1000);
      return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
    },

    isThinking() {
      return !!(
        this.isInitializing ||
        this.analysisLoading ||
        this.chatLoading ||
        this.apiSending ||
        this.apiSendingTip ||
        this.outputStaging
      );
    },
  },

  watch: {
    // done 전까지는 자동 정지하지 않음
    isThinking(val, oldVal) {
      if (val && !oldVal) this.startThinkingSound();
    }
  },

  methods: {
    async autoConnectCamera() {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const cameras = devices.filter(d => d.kind === "videoinput");

      if (cameras.length === 0) return;

      const defaultCameraId = this.selectedDeviceId || cameras[0].deviceId;


      const stream = await navigator.mediaDevices.getUserMedia({
        video: { deviceId: defaultCameraId }
      });

      const video = this.$refs.wsVideo;
      if (video) {
        video.srcObject = stream;

        // 🔥 브라우저 자동재생 정책 우회: 반드시 필요
        await video.play();

        this.streamReady = true;
      }
    } catch (err) {
      console.error("카메라 자동 연결 실패:", err);
    }
  },
    // === 공통 정리 ===
cleanupAudioTTS() {
  try {
    if (this._fadeRAF) cancelAnimationFrame(this._fadeRAF);
    this._fadeRAF = null;
    this._fadeToken++;

    if (this.thinkingAudio) {
      this.thinkingAudio.pause();
      this.thinkingAudio.currentTime = 0;
      this.thinkingAudio.volume = 0;
    }
    if (this.doneAudio) {
      this.doneAudio.pause();
      this.doneAudio.currentTime = 0;
    }

    // 🔥 Web Speech API 완전 종료
    window.speechSynthesis.cancel();

    // 🔥 speakText 내부 queue 중지
    stopSpeaking(true);

  } catch (e) {
    console.warn("cleanupAudioTTS error:", e);
  }
}
,



    async connectToServer() {
      
      this.wsConnected = true;
      

      // 1) 먼저 카메라 시작
      if (!this.streamReady) {
        try {
          await this.startStream(this.selectedDeviceId);
        } catch (e) {
          console.error('[WS] 카메라 스트림 시작 실패:', e);
        }
      }

      // 2) 상태 초기화
      this.wsStatus = 'connecting';
      this.autoCalibrated = false;
      this.wsLastTipSig = '';
      this.wsSavedRoiSize = null;
      this.lastGrid = null;
      this.lastTipRoi = null;
      this.lastPayloadText = '';
      this.debugImage = null;
      this.roiPreview = null;

      // 3) 실제 WebSocket 연결
      //  - 프록시(/ws-aruco) 사용: connectWS();
      //  - 로컬 서버 직접: connectWS("ws://localhost:8765");
      
    },

    disconnectFromServer() {
      disconnectWS();
      this.wsConnected = false;
      this.wsStatus = 'disconnected';
      this.autoCalibrated = false;

      // 카메라도 같이 정지
      this.stopStream();
    },

    // handleArrowDownWS(e) {
    //   if ((e.code === 'ArrowDown' || e.key === 'j' || e.key === 'J') && isOpen()) {
    //     e.preventDefault();
    //     this.lastSaveRoiAt = Date.now(); // 윈도우 시작
    //     sendJSON({ type: 'save_roi', timestamp: this.lastSaveRoiAt });
    //     this.wsSaveStatus = '저장 요청됨...';
    //   }
    // },
    handleWSOpen() {
      this.wsConnected = true;
      this.wsStatus = 'connected';

      this.wsLastTipSig = '';
      this.wsSavedRoiSize = null;
      this.lastGrid = null;
      this.lastTipRoi = null;
      this.lastPayloadText = '';
      this.debugImage = null;
      this.roiPreview = null;
      this.autoCalibrated = false;

      // ✅ 연결 직후 자동 캘리브레이션 (스냅샷 1회)
      setTimeout(() => {
        if (!this.autoCalibrated && this.streamReady) {
          console.log('[WS] auto calibrate after connect');
          this.onClickCalibrate();
        }
      }, 800); // 카메라 안정화 시간
    },

    handleWSClose() {
      this.wsConnected = false;
      this.wsStatus = 'disconnected';
      this.autoCalibrated = false;
    },

    handleWSMessage(data) {
      if (!data || typeof data !== 'object') return;

      if (data.type === 'calibrate_result') {
        if (data.ok) {
          console.log('ROI 캘리브레이션 성공:', data.roi_w, data.roi_h);

          this.wsSavedRoiSize = {
            width: data.roi_w,
            height: data.roi_h,
            roi_w: data.roi_w,
            roi_h: data.roi_h,
          };

          if (data.roi_image) {
            this.roiPreview = 'data:image/jpeg;base64,' + data.roi_image;
          }
          if (data.debug_image) {
            this.debugImage = 'data:image/jpeg;base64,' + data.debug_image;
          }

          this.autoCalibrated = true;
          this.wsSaveStatus = '캘리브레이션 완료';
        } else {
          this.wsSaveStatus = '캘리브레이션 실패';
          console.error('캘리브레이션 실패:', data.reason);
          //this.playDoneThenSpeak("キャリブレーションに失敗しました。もう一度お試しください。");
        }

      } else if (data.type === 'capture_result') {
        if (data.ok) {
          console.log('capture_result:', data);

          this.wsSavedRoiSize = {
            width: data.roi_w,
            height: data.roi_h,
            roi_w: data.roi_w,
            roi_h: data.roi_h,
          };
          this.lastGrid = data.grid || null;

          if (data.roi_image) {
            this.roiPreview = 'data:image/jpeg;base64,' + data.roi_image;
          }
          if (data.debug_image) {
            this.debugImage = 'data:image/jpeg;base64,' + data.debug_image;
          }

          const tip_roi = data.tip_roi;
          this.lastTipRoi = Array.isArray(tip_roi) ? tip_roi : null;

          if (this.lastTipRoi) {
            this._sendTipIfNotDup(this.lastTipRoi);
            // this.sendTipToApi(this.lastTipRoi);
            // F3용 대기 좌표 세팅
            this.pendingTip = this.lastTipRoi;
            // 혹시 이미 음성 쿼리가 준비되어 있으면 지금 바로 전송
            this.trySendF3Query();
          } else {
            // tip_roi 자체가 없을 때도 실패 처리
            const msg = '指の位置を正しく認識できませんでした。指をDotPadの所望の位置に置き直し、もう一度F3ボタンを押してください。';//손가락 위치를 제대로 인식하지 못했습니다. 손가락을 DotPad 위 원하는 위치에 다시 올린 후, F3 버튼을 다시 눌러주세요.
            console.error('손가락 캡처 실패: tip_roi 없음');
            this.addMessage('system', msg);
            //this.playDoneThenSpeak(msg);
          }

        } else {
          // 🔴 여기: 서버가 ok: false 로 보낸 경우 (예: "검지 손가락을 찾지 못했습니다.")
          console.error('손가락 캡처 실패:', data.reason);
          const msg =  '指の認識に失敗しました。';//손가락 인식에 실패했습니다.
          this.addMessage('system', `${msg} (사유: ${data.reason || '알 수 없음'})`);
          //this.playDoneThenSpeak(msg);
        }
      }
    },

    // === Tip 전송 ===
    _sendTipIfNotDup(tip) {
      
      const sig = `${tip[0].toFixed(2)},${tip[1].toFixed(2)}`;
      if (sig === this.wsLastTipSig) return;
      this.wsLastTipSig = sig;
      this.F3_sendTipToApi = tip;
    },

    // async sendTipToApi(tip) {
    //   console.log("sendTipToApi", tip);
    //   if (!tip || tip.length < 2) return;
    //   if (!this.wsSavedRoiSize || !this.wsSavedRoiSize.width) {
    //     this.pendingTip = tip; // ROI 아직이면 보류
    //     return;
    //   }

    //   this.apiSendingTip = true;
    //   this.apiErrorTip = '';
    //   this.apiResultTip = '';
    //   this.addMessage('user', this.F3_currentMessage);
    //   try {
    //     const res = await ApiService.sendFingerTip(tip, this.wsSavedRoiSize, this.F3_currentMessage);

    //     console.log("res = await ApiService.sendFingerTip", res);
    //     var text = res['text'];
    //     var braille = res['braille'];

    //     // 채팅에 표시할 답변 텍스트 추출
    //     const answer =
    //       text?.text ||
    //       text?.message ||
    //       text?.answer ||
    //       (typeof text === 'string' ? text : '좌표 전송이 완료되었습니다.');

    //     this.addMessage('system', `손가락 좌표를 API로 전송했습니다: ${JSON.stringify(tip)}`);
        
    //     this.addMessage('assistant', answer);
    //     // 상단 상태 텍스트(선택)
    //     this.apiResultTip = typeof text === 'string' ? text : (text?.message || '전송 완료');
    //     this.translate(braille);
        
    //   } catch (e) {
    //     this.apiErrorTip = e?.message || String(e);
    //     this.addMessage('system', `손가락 좌표 전송 실패: ${this.apiErrorTip}`);
    //   } finally {
    //     this.apiSendingTip = false;
    //   }
    // },
    async sendTipToApi(tip, overrideMessage) {
      console.log("sendTipToApi called with", tip, overrideMessage);

      

      const message =
        (overrideMessage && overrideMessage.trim()) ||
        (this.F3_currentMessage && this.F3_currentMessage.trim()) ||
        (this.currentMessage && this.currentMessage.trim()) ||
        '';

      this.apiSendingTip = true;
      this.apiErrorTip = '';
      this.apiResultTip = '';

      if (message) {
        this.addMessage('user', message);
      }

      try {
        console.log("res = await ApiService.sendFingerTip", tip, this.wsSavedRoiSize, message);
        const res = await ApiService.sendFingerTip(tip, this.wsSavedRoiSize, message);

        console.log("res = await ApiService.sendFingerTip", res);
        var text = res['text'];
        var braille = res['braille'];

        const answer =
          text?.text ||
          text?.message ||
          text?.answer ||
          (typeof text === 'string' ? text : '좌표 전송이 완료되었습니다.');

        this.addMessage('system', `손가락 좌표를 API로 전송했습니다: ${JSON.stringify(tip)}`);
        this.addMessage('assistant', answer);

        this.apiResultTip = typeof text === 'string' ? text : (text?.message || '전송 완료');
        //this.translate(braille);

      } catch (e) {
        this.apiErrorTip = e?.message || String(e);
        const msg =
          '손가락 좌표 전송에 실패했습니다. 네트워크 상태를 확인한 뒤, F3 버튼을 다시 눌러 시도해 주세요.';
        this.addMessage('system', `${msg} (오류: ${this.apiErrorTip})`);
        //this.playDoneThenSpeak(msg);   // 🔴 thinking 중지 + 안내음성
      } finally {
        this.apiSendingTip = false;
      }
    },


async snapshotAndSend(type) {
  console.log(`[SNAP] ${type} 시작`);
  const video = this.$refs.wsVideo;
  const canvas = this.$refs.wsCanvas;

  if (!video || !canvas) return;
  if (!video.videoWidth || !video.videoHeight) return;

  const ctx = canvas.getContext("2d");
  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;
  ctx.drawImage(video, 0, 0);

  const dataUrl = canvas.toDataURL("image/jpeg", 0.6);
  const base64 = dataUrl.split(",")[1];

  try {
    let res;

    if (type === "calibrate_roi") {
  res = await ApiService.calibrateROI(base64);

  this.wsSavedRoiSize = [res.roi_w, res.roi_h];
  this.savedHomography = res.H_mat;
  console.log("[CALIB] ROI:", res);

  const [roiW, roiH] = this.wsSavedRoiSize;

  // base64 → 이미지 변환
  const img = new Image();
  img.src = "data:image/jpeg;base64," + base64;

  img.onload = () => {
    // 원본 이미지 캔버스
    const fullCanvas = document.createElement("canvas");
    fullCanvas.width = img.width;
    fullCanvas.height = img.height;

    const fullCtx = fullCanvas.getContext("2d");
    fullCtx.drawImage(img, 0, 0);

    // 👉 OpenCV로 투시변환 준비
    const src = cv.imread(fullCanvas);
    const dst = new cv.Mat();
    const dsize = new cv.Size(roiW, roiH);

    // Homography 행렬을 OpenCV 형식으로 변환
    let H = this.savedHomography;
    if (!H || !H[0] || !H[1] || !H[2]) {
  console.error("❌ Homography matrix is missing:", H);
  //this.playDoneThenSpeak("ArUcoマーカーの認識に失敗しました。");
  return;
}
    let Hmat = cv.matFromArray(3, 3, cv.CV_64F, [
      H[0][0], H[0][1], H[0][2],
      H[1][0], H[1][1], H[1][2],
      H[2][0], H[2][1], H[2][2]
    ]);

    // 🔥 아루코 마커 기반 정확한 ROI 생성
    cv.warpPerspective(src, dst, Hmat, dsize, cv.INTER_LINEAR);

    // 캔버스에 출력
    const roiCanvas = document.createElement("canvas");
    roiCanvas.width = roiW;
    roiCanvas.height = roiH;

    cv.imshow(roiCanvas, dst);

    // ROI 미리보기 저장
    this.roiPreview = roiCanvas.toDataURL("image/jpeg", 0.9);

    // 메모리 정리
    src.delete();
    dst.delete();
    Hmat.delete();
  };
}

else if (type === "capture_tip") {
  console.log("capture_tip", this.wsSavedRoiSize, this.savedHomography);

  // --- 값 체크: 둘 중 하나라도 undefined면 API 호출 스킵 ---
  const noRoi = !this.wsSavedRoiSize || this.wsSavedRoiSize.some(v => v === undefined);
  const noHomo = !this.savedHomography || this.savedHomography.some(v => v === undefined);

  if (noRoi || noHomo) {
    console.warn("[TIP] ROI 또는 Homography 값이 없어 API 호출 스킵");
    this.pendingTip = null;   // 값 없다는 의미로 유지
    return;                   // API 호출하지 않음
  }

  // --- 정상적으로 값이 있을 때만 API 요청 ---
  const res = await ApiService.captureFingerTip(
    base64,
    this.wsSavedRoiSize,
    this.savedHomography
  );

  console.log("[TIP] ROI tip:", res.tip_roi, "grid:", res.grid);
  this.pendingTip = res.tip_roi;
}


  } catch (err) {
    console.error("API 실패:", err);
    //this.playDoneThenSpeak("情報の抽出に失敗しました。もう一度お試しください。");
  }
}
,


    // trySendF3Query() {
    //   // 좌표 준비 안 됨
    //   console.log("trySendF3Query", this.pendingTip, this.wsSavedRoiSize, this.pendingQuery);
    //   // if (!this.pendingTip || this.pendingTip.length < 2) return;
    //   // // ROI 정보 준비 안 됨
    //   // if (!this.wsSavedRoiSize || !this.wsSavedRoiSize.width) return;
    //   // // 음성 쿼리 준비 안 됨
    //   // if (!this.pendingQuery || !this.pendingQuery.trim()) return;

    //   const tip = this.pendingTip;
    //   const message = this.pendingQuery.trim();

    //   console.log('[F3] 좌표 + 음성 쿼리 전송:', tip, message);

    //   // 실제 API 호출
    //   this.sendTipToApi(tip, message);

    //   // 한 번 보냈으면 비워주기
    //   this.pendingTip = null;
    //   this.pendingQuery = '';
    // },
    trySendF3Query() {
      // 좌표 준비 안 됨
      console.log("trySendF3Query", this.pendingTip, this.wsSavedRoiSize, this.pendingQuery);
      // if (!this.pendingTip || this.pendingTip.length < 2) return;
      // // ROI 정보 준비 안 됨
      // if (!this.wsSavedRoiSize || !this.wsSavedRoiSize.width) return;
      // // 음성 쿼리 준비 안 됨
      // if (!this.pendingQuery || !this.pendingQuery.trim()) return;
      const tip = this.pendingTip;
      console.log("tip", tip);
      const message = this.pendingQuery.trim();
      this.currentMessage = this.pendingQuery.trim();
      if (tip == null) {      // null 또는 undefined 모두 체크
        this.sendMessage();
        return;               // 아래 코드 실행하지 않도록 종료
      }
      console.log('[F3] 좌표 + 음성 쿼리 전송:', tip, message);
      // 실제 API 호출
      this.sendTipToApi(tip, message);
      // 한 번 보냈으면 비워주기
      this.pendingTip = null;
      this.pendingQuery = '';
    },

    onClickCalibrate() {
      this.snapshotAndSend('calibrate_roi');
    },

    onClickCaptureAndSend() {
      this.snapshotAndSend('capture_tip');
    },


    // === Dotpad events ===
    onDotpadStateChanged(e) {
      this.currentDotpadState = (e?.detail?.state) || dotpadState;
      this.printPixels();
    },
    async onDotpadTTSChanged(e) {
      this.currentDotpadTTS = (e?.detail?.tts) || dotpadTTS;
      switch (this.currentDotpadTTS) {
        case 'R': // 다음 페이지
          this.currentBraillePageIndex++;
          if (this.currentBraillePageIndex >= this.braillePages.length) {
            

            this.currentBraillePageIndex = 0;   // 마지막에서 → 처음으로 순환
          }
          this.showBraillePage(this.currentBraillePageIndex);
          break;

        case 'L': // 이전 페이지
          this.currentBraillePageIndex--;
          if (this.currentBraillePageIndex < 0) {
            this.currentBraillePageIndex = this.braillePages.length - 1; // 처음에서 → 끝으로 순환
          }
          this.showBraillePage(this.currentBraillePageIndex);
          break;
      
        case 'S': togglePlayPause(this.rate); break;


    
        //case 'L': replayPrevious(this.rate); break;
        //case 'R': skipSentence(this.rate); break;
        default: break;
      }
    },
    // onDotpadwebCamChanged(e) {
    //   this.currentDotpadwebCam = true;
    //   this.lastSaveRoiAt = Date.now(); // 윈도우 시작
    //     sendJSON({ type: 'save_roi', timestamp: this.lastSaveRoiAt });
    //     this.wsSaveStatus = '저장 요청됨...';
    //   // this.currentDotpadwebCam = (e?.detail?.webCam) || dotpadwebCam;
    //   // if (this.currentDotpadwebCam) {
    //   //   this.lastSaveRoiAt = Daㅔㅛ소te.now(); // 윈도우 시작
    //   //   sendJSON({ type: 'save_roi', timestamp: this.lastSaveRoiAt });
    //   //   this.wsSaveStatus = '저장 요청됨...';
    //   // }
    // },

    onDotpadStartListening(e) {
      this.currentDotpadStartListening = true;
      if (!this.isListening && !this.chatLoading) {
         
        this.finger_startListening();
      }
      console.log("F3 눌렀을 때만 진행되나 시작");
      console.log("start listening...", this.currentDotpadStartListening);
    },

    onDotpadStopListening(e) {
      this.currentDotpadStopListening = true;
      if (this.isListening) {
        this.stopListening();
      }
      console.log("stop listening...", this.currentDotpadStopListening);
    },

    // 점자 텍스트 출력
    runTranslate() {
      const text = String(this.tx ?? "");
      this.out = typeof this.translate === "function" ? this.translate(text) : text;
    },
    translate(text) {
      ApiService.translate(text).then(response => {
        const labels = response["text"];
        const dotpadList = DotpadList.getList();
        if (dotpadList.length > 0) {
          for (const dotPad of dotpadList) {
            const padHex = (data, totalLen) => data.padEnd(totalLen, "0");
            const filled = padHex(labels, 40);
            dotPad.dotpadSDK.dotPadSendModule.setDotPadLineCommand(0, "80", "00", filled);
            dotPad.dotpadSDK.dotPadSendModule.setRefreshMode("TEXT_DISPLAY");
            dotPad.dotpadSDK.dotPadSendModule.sendCommand();
          }
        }
      }).catch(error => {
        console.error('AI 요청 실패:', error);
        this.playDoneThenSpeak("情報の抽出に失敗しました。もう一度お試しください。");
        this.chatLoading = false;
      });
    },

 splitHexIntoPages(hex) {
  const cells = [];             // 2글자씩 분리된 셀 목록
  for (let i = 0; i < hex.length; i += 2) {
    cells.push(hex.substring(i, i + 2));
  }

  const pages = [];
  let current = [];

  for (let i = 0; i < cells.length; i++) {
    const c = cells[i];

    // 🔥 000000 (00 00 00) 단락 구분
    if (c === "00" && cells[i+1] === "00" && cells[i+2] === "00") {
      // 현재 페이지 push
      if (current.length > 0) pages.push(current.join(""));

      current = [];  // 새 페이지 시작
      i += 2;        // 3칸(000000) 건너뛰기
      continue;
    }

    current.push(c);

    // 🔥 20셀에 도달하면 무조건 페이지 완성
    if (current.length === 20) {
      pages.push(current.join(""));
      current = [];
    }
  }

  // 마지막 페이지도 푸시
  if (current.length > 0) {
    pages.push(current.join(""));
  }

  return pages;
},

async translate_QA(text) {
  ApiService.translate(text).then(response => {

    let hex = response["text"].replace(/\s+/g, "");
    console.log("HEX FROM SERVER:", hex);

    // --------------------------
    // 1) 2글자씩 묶어서 20셀 or 000000 기준 split
    // --------------------------
    this.braillePages = this.splitHexIntoPages(hex);

    // --------------------------
    // 2) 첫 페이지 출력
    // --------------------------
    this.currentBraillePageIndex = 0;
    this.showBraillePage(0);

  }).catch(err => {
    console.error(err);
    this.playDoneThenSpeak("情報の抽出に失敗しました。もう一度お試しください。");
  });
},



async showBraillePage(pageIndex) {
  console.log("pageIndex", pageIndex);
  if (pageIndex === 0) {
  if (this.doneAudio) {
    this.doneAudio.currentTime = 0;
    await this.doneAudio.play().catch(() => {});
  }
}

  const dotpadList = DotpadList.getList();
  if (dotpadList.length === 0) return;

  const text = this.braillePages[pageIndex] || "";
  const filled = text.padEnd(40, "0");
  
  for (const dotPad of dotpadList) {

    // DotPad 명령은 반드시 await 필요
    await new Promise(resolve => setTimeout(resolve, 120));

    dotPad.dotpadSDK.dotPadSendModule.setDotPadLineCommand(
      0, "80", "00", filled
    );

    dotPad.dotpadSDK.dotPadSendModule.setRefreshMode("TEXT_DISPLAY");

    // 가장 중요한 부분: sendCommand는 반드시 await 필요
    await dotPad.dotpadSDK.dotPadSendModule.sendCommand();
  }
},



    // 촉각 그래픽 전송
    printPixels() {
      let pixels = [];
      const state = this.currentDotpadState ?? dotpadState;
      if (state === "1") pixels = this.legend;
      else if (state === "2") pixels = this.chart;
      else return;

      if (!Array.isArray(pixels) || pixels.length === 0) return;

      const dotpadList = DotpadList.getList();
      if (!Array.isArray(dotpadList) || dotpadList.length === 0) return;

      for (const dotPad of dotpadList) {
        try {
          dotPad.dotpadSDK.Load_mapFile(pixels);
          dotPad.dotpadSDK.Make_DTM_Data();
          dotPad.dotpadSDK.sendPixelPattern();
        } catch (err) { console.error('Dotpad 전송 오류:', err); 
          this.playDoneThenSpeak("情報の抽出に失敗しました。もう一度お試しください。");
        }
      }
    },


    flattenArray(originalArray, targetWidth, targetHeight) {
      const flattenedArray = new Array(targetWidth * targetHeight).fill(0);
      const inputWidth = originalArray[0].length;
      const inputHeight = originalArray.length;
      const startX = Math.floor((targetWidth - inputWidth) / 2);
      const startY = Math.floor((targetHeight - inputHeight) / 2);

      for (let y = 0; y < inputHeight; y++) {
        for (let x = 0; x < inputWidth; x++) {
          const index = (startY + y) * targetWidth + (startX + x);
          flattenedArray[index] = originalArray[y][x];
        }
      }
      return flattenedArray;
    },

    loadImageData() {
      const q = this.$route.query;
      this.imageData = {
        id: this.$route.params.id || q.id || '',
        rId: q.rId || '',
        objectType: q.objectType || '',
        file: q.file || '',
        name: q.name || '이미지',
        size: q.size ? parseInt(q.size) : null,
        isUploaded: q.isUploaded === 'true',
        isAnalyzed: q.isAnalyzed === 'true',
        analyzed: q.analyzed ? JSON.parse(q.analyzed) : null
      };
      const v = q.ours_or_baseline;
      if (v !== undefined) this.ours = (v === true || v === 'true' || v === '1');
    },

    // === 분석 파이프라인: printPixels → done → speak ===
    async processAnalysisData() {
      this.analysisLoading = true;
      const imageUrl = this.imageData.file || this.imageData.url;
      if (!imageUrl) {
        this.imageCharacteristics = '이미지 경로가 존재하지 않아 분석할 수 없습니다.';
        this.analysisLoading = false;
        return;
      }

      try {
        const analysisResult = ApiService.Image_Descriptor(imageUrl);
        this.imageData.isAnalyzed = true;
        this.imageData.analyzed = await analysisResult;

        ApiService.analyzeImage(imageUrl, this.ours ? '1' : '0')
          .then(async (response) => {
            if (!this.isAlive) return;
            this.outputStaging = true; // thinking 유지

            labels = response["resized_list"];
            this.chart = this.flattenArray(labels, 60, 40);
            this.legend = this.flattenArray(response["legend"], 60, 40);

            if (this.ours) {
              this.currentDotpadState = "1";
              this.printPixels();
            }
            if (!this.isAlive) return;
            this.imageData.data = this.chart;
            this.translate(response["type"]);

            this.outputStaging = false;
            if (!this.isAlive) return;
            // 이제 done → speak
            await this.playDoneThenSpeak(this.imageData.analyzed);
          })
          .catch(error => console.error('AI 분석 실패:', error));
      } catch (error) {
        console.error('❌ 분석 중 오류 발생:', error);
        await this.playDoneThenSpeak("情報の抽出に失敗しました。もう一度お試しください。");//정보 추출에 실패 했습니다. 다시 시도해주세요.
        this.imageCharacteristics = '이미지 분석 중 오류가 발생했습니다: ' + error.message;
        this.cleanupAudioTTS();
      } finally {
        this.analysisLoading = false;
      }
    },

    // === 채팅 ===
    async sendMessage() {
      const imageUrl = this.imageData.file || this.imageData.url;
      if (!imageUrl) {
        this.imageCharacteristics = '이미지 경로가 존재하지 않아 분석할 수 없습니다.';
        this.analysisLoading = false;
        return;
      }

      const userMessage = this.currentMessage.trim();
      if (!userMessage || this.chatLoading) return;

      this.currentMessage = '';
      console.log("this.currentMessage", this.currentMessage)
      this.addMessage('user', userMessage);
      this.chatLoading = true;

      console.log('sendChatMessage:', userMessage);
    
      ApiService.sendChatMessage(imageUrl, userMessage).then(response => {
        if (!Array.isArray(response.resized_list) || response.resized_list.length === 0) {
          return;
        }

        let labels = response["resized_list"];
        console.log('AI 분석 결과:', labels);
        this.chart = this.flattenArray(labels, 60, 40);
        if (this.ours) {
        this.printPixels();}
        this.translate_QA(response['text']);
        let assistant_result = response["text"];
        console.log('assistant_result 분석 결과:', assistant_result);

        // ✅ 응답을 받은 후 메시지를 추가하도록 위치 이동
        this.addMessage('assistant', assistant_result, this.chart);
        this.chatLoading = false;
      }).catch(error => {
        console.error('AI 요청 실패:', error);
        this.playDoneThenSpeak("質問に関する情報の抽出に失敗しました。もう一度お試しください");//질문에 대한 정보 추출에 실패 했습니다. 다시 시도해주세요.
        this.chatLoading = false;
      });

    },


    //AI 대화 
   
    askQuestion(question) {
      this.currentMessage = question
      this.sendMessage()
    },

    addMessage(type, content, labels = null) {
      const message = {
        id: this.messageIdCounter++,
        type,
        content,
        labels,
        timestamp: new Date()
      }
      this.chatMessages.push(message)
      // 자동 TTS: assistant 메시지일 때 자동 재생
      if (this.ttsEnabled && type === 'assistant' && content) {
        speakText(content, this.rate, this.speechLangCode);
      }
      // 메시지 영역 스크롤을 맨 아래로
      this.$nextTick(() => {
        const messagesEl = this.$refs.chatMessages
        if (messagesEl) {
          messagesEl.scrollTop = messagesEl.scrollHeight
        }
      })
    },
    // TTS on/off 토글
    toggleTTS() {
      this.ttsEnabled = !this.ttsEnabled;
      if (!this.ttsEnabled) {
        stopSpeaking();
      }
    },
    selectOurs() {
      this.ours = true;
      // localStorage에 상태 저장
      localStorage.setItem('oursBaselineState', 'ours');
      console.log("✅ Ours 선택됨 및 저장됨");
    },
    selectBaseline() {
      this.ours = false;
      // localStorage에 상태 저장
      localStorage.setItem('oursBaselineState', 'baseline');
      this.playBaseline(); // Baseline 실행
      console.log("✅ Baseline 선택됨 및 저장됨");
    },

    // ours/baseline 상태 불러오기 (localStorage에서)
    loadOursBaselineState() {
      const saved = localStorage.getItem('oursBaselineState');
      if (saved) {
        this.ours = saved === 'ours';
        console.log('📂 저장된 Ours/Baseline 상태 불러옴:', saved);
      }
    },

    // TTS rate 선택 및 저장
    selectRate(num) {
      this.rate = num;
      // localStorage에 rate 상태 저장
      localStorage.setItem('ttsRateState', num.toString());
      console.log('✅ TTS Rate 선택됨 및 저장됨:', num);
    },

    // TTS rate 상태 불러오기 (localStorage에서)
    loadRateState() {
      const saved = localStorage.getItem('ttsRateState');
      if (saved) {
        const rateNum = parseInt(saved);
        // 유효한 rate 값인지 확인 (2, 5, 8, 12, 15 중 하나)
        if ([0.5, 1, 1.25, 1.5, 2].includes(rateNum)) {
          this.rate = rateNum;
          console.log('📂 저장된 TTS Rate 상태 불러옴:', rateNum);
        }
      }
    },
    async playBaseline(){
      try {
        const response = await ApiService.sendChatMessage(imageUrl, userMessage);
        if (!Array.isArray(response.resized_list) || response.resized_list.length === 0) return;

        const lbls = response["resized_list"];
        this.chart = this.flattenArray(lbls, 60, 40);
        const assistant_result = response["text"];

        // assistant 메시지 추가: printPixels → done → speak 순서 보장
        this.addMessage('assistant', assistant_result, this.chart);
      } catch (e) {
        console.error('AI 요청 실패:', e);
        this.cleanupAudioTTS();
      } finally {
        this.chatLoading = false;
      }
    },

    // 메시지 추가(assistant이면 출력 완료 후 done→speak)
    addMessage(type, content, labels = null) {
      const msg = { id: this.messageIdCounter++, type, content, labels, timestamp: new Date() };
      this.chatMessages.push(msg);

      if (type === 'assistant') {
        this.outputStaging = true;

        if (labels && this.ours) {
          this.chart = labels;
          this.currentDotpadState = "2"; // 차트 모드
          this.printPixels();
        }

        this.outputStaging = false;

        if (this.ttsEnabled && content) this.playDoneThenSpeak(content);
      }

      this.$nextTick(() => {
        const el = this.$refs.chatMessages;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },

    // TTS/voice helpers
    toggleTTS() { this.ttsEnabled = !this.ttsEnabled; if (!this.ttsEnabled) stopSpeaking(); },
    selectOurs() { this.ours = true; localStorage.setItem('oursBaselineState', 'ours'); },
    selectBaseline() { this.ours = false; localStorage.setItem('oursBaselineState', 'baseline'); this.playBaseline(); },
    loadOursBaselineState() { const saved = localStorage.getItem('oursBaselineState'); if (saved) this.ours = saved === 'ours'; },
    selectRate(num) { this.rate = num; localStorage.setItem('ttsRateState', num.toString()); },
    loadRateState() { const s = localStorage.getItem('ttsRateState'); if (s) { const n = parseInt(s); if ([2,5,8,12,15].includes(n)) this.rate = n; } },

    async playBaseline() {
      try {
        const data = await import(`@/assets/json/${this.imageData.name}.json`);
        this.gridData = data.default;
      } catch (e) { console.error("JSON 로드 실패:", e); }
      this.chart = this.flattenArray(this.gridData, 60, 40);
      this.printPixels();
    },

    playAnalysisTTS() {
      if (!this.imageData.analyzed) return;
      this.outputStaging = true;
      if (this.ours && this.imageData.data) { this.chart = this.imageData.data; this.printPixels(); }
      this.outputStaging = false;
      this.playDoneThenSpeak(this.imageData.analyzed);
    },
    playChatTTS(message) {
      if (!(message && message.type === 'assistant' && message.content)) return;
      this.outputStaging = true;
      if (message.labels && this.ours) { this.chart = message.labels; this.printPixels(); }
      this.outputStaging = false;
      this.playDoneThenSpeak(message.content);
    },

    // 라우팅
    goBack() {
      this.cleanupAudioTTS();
      this.$router.back();

    },
    formatSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024, units = ['Bytes','KB','MB','GB'], i = Math.floor(Math.log(bytes)/Math.log(k));
      return `${parseFloat((bytes/Math.pow(k,i)).toFixed(2))} ${units[i]}`;
    },
    formatTime(date) { return date.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' }) },
    toggleJsonExpanded() { this.jsonExpanded = !this.jsonExpanded; },
    formatAnalysisData(data) {
      if (!data) return '분석 데이터가 없습니다.';
      try { return (typeof data === 'string') ? data : JSON.stringify(data, null, 2); }
      catch { return '데이터 포맷 오류'; }
    },

    initWebSpeech(mode = 'chat') {
      const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (!SR) {
        this.sttError = '이 브라우저는 Web Speech API(음성 인식)를 지원하지 않습니다.';
        console.warn('Web Speech API not supported');
        return null;
      }

      if (!this.recognition) {
        const rec = new SR();
        rec.lang = this.speechLangCode;  // 동적 언어 설정
        rec.interimResults = true;
        rec.continuous = false;

        rec.onstart = () => {
          console.log('[WebSpeech] onstart');
          this._speechStarting = false;   // ✅ start 단계 끝남
          this.isListening = true;
          this.sttError = null;
          this.transcription = '';
        };

        rec.onresult = (event) => {
          let finalText = '';
          for (let i = event.resultIndex; i < event.results.length; i++) {
            const res = event.results[i];
            if (res.isFinal) {
              finalText += res[0].transcript;
            } else {
              this.transcription = res[0].transcript;
            }
          }
          finalText = finalText.trim();
          if (!finalText) return;

          console.log('🎤 Web Speech final:', finalText);

          if (this.sttMode === 'chat') {
            this.currentMessage = finalText;
            this.sendMessage();
          } else if (this.sttMode === 'f3') {
            this.F3_currentMessage = finalText;
            this.pendingQuery = finalText;
            this.trySendF3Query();
          }
        };

        rec.onerror = (e) => {
          console.error('Web Speech error:', e);
          this.sttError = '음성 인식 오류가 발생했습니다: ' + (e.error || e.message || e);
          this.isListening = false;
          this._speechStarting = false;   // ✅ 에러 시도도 종료 상태로
        };

        rec.onend = () => {
          console.log('[WebSpeech] onend');
          this.isListening = false;
          this._speechStarting = false;   // ✅ 완전히 끝났으니 다시 start 가능
          this.transcription = '';
        };

        this.recognition = rec;
      }

      this.sttMode = mode;
      return this.recognition;
    },


    // STT shortcuts
    handleSpaceDown(e) { if (e.code==='Space' && !this.isListening && !this.chatLoading) { e.preventDefault(); this.startListening(); } },
    handleSpaceUp(e)   { if (e.code==='Space' && this.isListening) { e.preventDefault(); this.stopListening(); } },
    handleCtrlShiftDown(e) {
      // 키 누르고 있는 동안 반복 발생하는 이벤트 무시
      if (e.repeat) return;

      if (e.ctrlKey && e.shiftKey && !this.chatLoading) {
        e.preventDefault();
        this.startListening();
      }
    },
    handleCtrlShiftUp(e)   { if (this.isListening) { e.preventDefault(); this.stopListening(); } },

    async finger_startListening() {
      console.log('finger_startListening() (Web Speech) 시작');

      // 🔥 이미 듣고 있거나, 시작 중이면 무시
      if (this.isListening || this.chatLoading || this._speechStarting) {
        console.log('🚫 이미 STT 진행 중 (F3 모드)');
        return;
      }

      // 1) F3 상태 리셋
      this.pendingTip = null;
      this.pendingQuery = '';
      this.F3_currentMessage = '';
      this.wsLastTipSig = '';

      // 2) 프레임 캡처 → tip 계산 요청
      if (this.wsConnected) {
        console.log('F3 시점 프레임 캡처 (capture_tip)');
        this.snapshotAndSend('capture_tip');
      } else {
        console.warn('[F3] WS 연결이 없어 tip 캡처를 건너뜁니다.');
      }

      // 3) 음성 인식 시작
      const rec = this.initWebSpeech('f3');
      if (!rec) return;

      try {
        this._speechStarting = true;
        rec.start();
      } catch (err) {
        console.error('rec.start() error:', err);
        this.sttError = '음성 인식 시작 오류: ' + (err.message || err);
        this._speechStarting = false;
      }
    },

    async startListening() {
      console.log('startListening() (Web Speech)');

      // 🔥 이미 듣고 있거나, 시작 중이면 다시 시작하지 않기
      if (this.isListening || this.chatLoading || this._speechStarting) {
        console.log('🚫 이미 STT 진행 중 (isListening or _speechStarting)');
        return;
      }

      const rec = this.initWebSpeech('chat');
      if (!rec) return;

      try {
        this._speechStarting = true;   // ✅ start 호출 시작
        rec.start();
      } catch (err) {
        console.error('rec.start() error:', err);
        this.sttError = '음성 인식 시작 오류: ' + (err.message || err);
        this._speechStarting = false;  // 실패했으니 다시 초기화
      }
    },

    async stopListening() {
      console.log('stopListening() (Web Speech)');
      try {
        if (this.recognition && (this.isListening || this._speechStarting)) {
          this.recognition.stop();
        }
      } catch (e) {
        console.warn('recognition.stop() error:', e);
      } finally {
        // onend에서 최종 정리하지만, 혹시 못 들어갈 때를 대비
        this.isListening = false;
        this._speechStarting = false;
      }
    },

    // MediaStream
    stopStream() { try { this.stream?.getTracks()?.forEach(t => t.stop()); } catch {}; this.stream=null; this.streamReady=false; },
    
    async startStream(deviceId) {
      this.stopStream();
      const constraints = deviceId
        ? { video: { deviceId: { exact: deviceId } }, audio: false }
        : { video: true, audio: false };

      try {
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        this.stream = stream;
        this.streamReady = true;

        const videoEl = this.$refs.wsVideo;
        if (videoEl) {
          videoEl.srcObject = stream;
          try {
            await videoEl.play();
          } catch (err) {
            console.error('[Camera] video.play() 에러:', err);
          }
        }

        await this.refreshDevices();
      } catch (e) {
        console.error('getUserMedia 실패:', e);
        this.streamReady = false;
      }
    },

    // async refreshDevices() {
    //   try {
    //     const all = await navigator.mediaDevices.enumerateDevices();
    //     this.devices = all.filter(d => d.kind === "videoinput");
    //     if (!this.selectedDeviceId && this.devices.length) this.selectedDeviceId = this.devices[0].deviceId;
    //   } catch (e) { console.error("enumerateDevices 실패:", e); }
    // },
   async refreshDevices() {
  try {
    const all = await navigator.mediaDevices.enumerateDevices();
    this.devices = all.filter(d => d.kind === "videoinput");

    if (this.devices.length === 0) return;

    // 외장 USB 웹캠 우선 매칭
    const webcam = this.devices.find(d =>
      /(usb|webcam|logitech|c922|c310|pc camera|external)/i.test(d.label) ||
      /(usb|external)/i.test(d.deviceId)
    );

    // 내장 카메라 후보
    const internal = this.devices.find(d =>
      /(integrated|internal|default|built)/i.test(d.label)
    );

    // 우선순위 선택
    let preferredDeviceId = null;

    if (webcam) preferredDeviceId = webcam.deviceId;
    else if (internal) preferredDeviceId = internal.deviceId;
    else preferredDeviceId = this.devices[0].deviceId;

    // 기존 선택과 다르면 갱신
    if (this.selectedDeviceId !== preferredDeviceId) {
      this.selectedDeviceId = preferredDeviceId;
    }

  } catch (e) {
    console.error("Device refresh error:", e);
  }
},


    async restartWithDevice() { if (!this.selectedDeviceId) return; await this.startStream(this.selectedDeviceId); },

    // Thinking loop
    startThinkingSound() {
      try {
        if (!this.thinkingAudio) return;
        this._fadeToken++; const myToken = this._fadeToken;
        if (this._fadeRAF) cancelAnimationFrame(this._fadeRAF);

        this.thinkingAudio.currentTime = 0;
        this.setVolumeSafe(0);

        this.thinkingAudio.play()
          .then(() => {
            const target = 0.08, dur = 180;
            const t0 = performance.now();
            const step = (now) => {
              if (myToken !== this._fadeToken) return;
              const p = Math.max(0, Math.min(1, (now - t0) / dur));
              this.setVolumeSafe(target * p);
              if (p < 1) this._fadeRAF = requestAnimationFrame(step);
            };
            this._fadeRAF = requestAnimationFrame(step);
          })
          .catch(err => console.warn('🔇 thinking play blocked:', err));
      } catch {}
    },
    stopThinkingImmediately() {
      try {
        this._fadeToken++;
        if (this._fadeRAF) cancelAnimationFrame(this._fadeRAF);
        this._fadeRAF = null;
        if (this.thinkingAudio) {
          this.thinkingAudio.pause();
          this.thinkingAudio.currentTime = 0;
          this.thinkingAudio.volume = 0;
        }
      } catch {}
    },
    setVolumeSafe(val) {
      if (!this.thinkingAudio) return;
      const n = Number.isFinite(val) ? val : 0;
      const clamped = Math.max(0, Math.min(1, n));
      this.thinkingAudio.volume = clamped;
    },

    // done → speak (done 직전에 thinking 즉시 정지)
    async playDoneThenSpeak(text) {
      try {
        this.stopThinkingImmediately();
        if (this.doneAudio) {
          this.doneAudio.currentTime = 0;
          await this.doneAudio.play().catch(() => {});
          await new Promise((res) => {
            const h = () => { this.doneAudio.removeEventListener('ended', h); res(); };
            this.doneAudio.addEventListener('ended', h, { once: true });
            setTimeout(res, 400);
          });
        }
      } catch {}
      if (text) speakText(text, this.rate, this.speechLangCode);
    },
  }
};
</script>




<style 
scoped>
.image-detail {
  height: calc(100vh - 100px);
  background: #ffffff;
  display: flex;
  flex-direction: column;
}


.detail-header h1 {
  color: #2c3e50;
  margin: 0;
  font-size: 1.5rem;
}

.back-btn {
  background: transparent;
  border: none;
  padding: 8px 12px;
  margin-right: 8px;
  font-size: 12px;
  font-weight: 600;
  color: #111827;
  cursor: pointer;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
}
.back-icon {
  font-size: 22px;
  font-weight: 600;
}
.back-btn:hover {
  background: rgba(0, 0, 0, 0.05);
}

.detail-header {
  background: #F7F7F7;
  padding: 0.75rem 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  cursor: pointer;     
  flex-shrink: 0;
  z-index: 100;
}

.detail-header h1 {
  color: #2c3e50;
  margin: 0;
  font-size: 1.5rem;
}
.detail-header:hover {
  background: #f3f4f6;   /* 연한 회색 */
}



.detail-content {
  display: grid;
  grid-template-columns: minmax(400px, 1fr) 2fr;
  gap: 1.5rem;
  overflow-y: auto;    /* ⬅ 스크롤 허용 */
  padding: 24px; /* 하단 여유 */
  padding-top: 32px;    /* ⭐ 이 줄이 핵심 */
  height: calc(100vh - 80px); /* 헤더 제외 높이 */
  flex-grow: 1;
}

.image-section {
  display: flex;
  justify-content: center;
  padding: 24px 0;
}

.image-card {
  width: 100%;
  max-width: 520px;
  background: #ffffff;
  border-radius: 20px;
  padding: 20px;

  display: flex;
  flex-direction: column;     /* 세로 정렬 */
  justify-content: center;    /* ⭐ 세로 중앙 */
}



.image-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.image-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  background: #f8fafc;
  border-radius: 16px;
  padding: 16px;
  border: 1px solid #e5e7eb;
}

.main-image {
  max-width: 100%;
  max-height: 320px;
  object-fit: contain;
  border-radius: 12px;
}


.main-image:hover {
  transform: scale(1.02);
}

.image-info {
  margin-top: 16px;
  padding: 16px;
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid #e5e7eb;  /* ⭐ 이미지 카드와 동일 */
}


.image-info h3 {
  margin: 0 0 0.5rem 0;
  color: #2c3e50;
  font-size: 1rem;
  font-weight: 600;
}

/* Read Aloud 버튼 */
.tts-btn {
  background: #ffffff;
  color: #111827;
  border: none;                 /* ❗ 테두리 제거 */
  border-radius: 999px;         /* pill */
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 1px 4px rgba(0,0,0,0.08);
  display: flex;
  align-items: center;
  gap: 6px;
}

.tts-btn:hover {
  background: #f3f4f6;
}


.image-info p {
  margin: 0.25rem 0;
  color: #6c757d;
  font-size: 0.85rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

/* ImageData 상세 정보 스타일 */
.imagedata-details {
  margin-top: 16px;
  padding: 16px;
  border-radius: 16px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
}

.imagedata-details h4 {
  margin: 0 0 12px 0;
  font-size: 14px;
  font-weight: 600;
  color: #111827;
}


/* 우측 분석 섹션 */
.analysis-section {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  height: 100%;
  min-height: 0; /* Flex 아이템이 넘칠 때 축소되도록 허용 */
}

.analysis-panel {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  flex-shrink: 0;
  overflow-y: auto;
  max-height: 45%;
}

.analysis-panel h3 {
  margin: 0 0 1rem 0;
  color: #2c3e50;
  font-size: 1.1rem;
}

/* 채팅 패널 */
.chat-panel {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: none;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
}

.chat-panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.chat-panel h3 {
  margin: 0;
  color: #2c3e50;
  font-size: 1.1rem;
}

.chat-messages {
  display: flex;              /* ⭐ 필수 */
  flex-direction: column;     /* ⭐ 필수 */
  flex: 1;
  overflow-y: auto;
  gap: 12px;
  background: transparent;
  padding: 8px 4px;
}


.chat-messages::-webkit-scrollbar {
  width: 10px;
}

.chat-messages::-webkit-scrollbar-track {
  background: #f1f3f4;
  border-radius: 5px;
  margin: 5px;
}

.chat-messages::-webkit-scrollbar-thumb {
  background: #c1c7cd;
  border-radius: 5px;
  border: 2px solid #f1f3f4;
}

.chat-messages::-webkit-scrollbar-thumb:hover {
  background: #9aa0a6;
}

.message {
  margin-bottom: 1rem;
  padding: 0.75rem;
  border-radius: 12px;
  max-width: 85%;
  word-wrap: break-word;
  animation: slideIn 0.3s ease-out;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}


/* ===============================
   CHAT – FINAL OVERRIDE
   =============================== */

.chat-messages {
  flex: 1;                 /* ⭐ 여기서만 늘어남 */
  overflow-y: auto;

  background: transparent;
  padding: 8px 4px;
}


/* 말풍선 공통 */
.message {
  max-width: 70% !important;
  padding: 10px 14px !important;
  border-radius: 14px !important;
  margin-bottom: 0 !important;
  box-shadow: none !important;
}

/* Assistant (왼쪽) */
.message.assistant {
  background: #f3f4f6 !important;
  color: #111827 !important;
  border: none !important;
  align-self: flex-start !important;
  margin-left: 4px;
}

/* User (오른쪽) */
.message.user {
  background: #f97316 !important;
  color: #ffffff !important;
  align-self: flex-end !important;
  text-align: left !important;
  margin-right: 4px; 
}
.ai-analysis-header {
  display: flex;
  align-items: center;
  justify-content: space-between; /* ⭐ 좌우 분리 핵심 */
  gap: 12px;

  padding-bottom: 8px;
}
/* 시간 */
.message-time {
  font-size: 11px !important;
  opacity: 0.6;
  margin-top: 4px;
  text-align: right;
}

/* 빈 상태 제거 */
.empty-chat-message {
  display: none !important;
}

/* STT 안내 바 */
.stt-indicator-bar {
  width: 100%;
 
  background: #fff1e6;              /* 연한 주황 */
  color: #f97316;

  padding: 10px 16px;
  font-size: 16px;
  font-weight: 700;

  border-radius: 14px 14px 0 0;     /* ⭐ 위만 둥글게 */
  
  margin-bottom: 12px;              /* 메시지와 간격 */

  display: flex;
  align-items: center;
  gap: 8px;
}
.chat-panel {
  background: #ffffff;
  border-radius: 14px;
  padding: 0;
  
  display: flex;
  flex-direction: column;
}
/* 카드 공통 – 그림자 제거, 라인만 */
.chat-panel,
.ai-analysis,
.tts-rate-wrapper {
  box-shadow: none !important;
  border: 1px solid #e5e7eb !important;
}



.message-content {
  margin-bottom: 0.25rem;
}

.chat-input-area {
  flex-shrink: 0;
  border-top: 1px solid #e5e7eb;
  padding: 12px 16px;
}


.input-container {
  display: flex;
  gap: 0.75rem;
  align-items: center;
}

.chat-input {
  flex: 1;
  padding: 0.75rem 1rem;
  border: 1px solid #dce4f0;
  border-radius: 8px;
  font-size: 0.9rem;
  transition: all 0.2s ease;
  background: white;
  resize: none;
}

.chat-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.send-btn {
  background: f97316;
  color: white;
  border: none;
  padding: 0.75rem 1.25rem;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.2s ease;
}

.send-btn:hover:not(:disabled) {
  background: #eb8525;
}

.send-btn:disabled {
  background: #f97316;
  cursor: not-allowed;
}

.suggested-questions {
  margin-bottom: 1rem;
  flex-shrink: 0;
}

.suggested-questions p {
  margin: 0 0 0.5rem 0;
  font-size: 0.9rem;
  color: #495057;
  font-weight: 600;
  text-align: left;
}

.suggested-questions-container {
  display: flex;
  gap: 0.5rem;
  overflow-x: auto;
  padding: 0.25rem 0;
  scrollbar-width: thin;
  scrollbar-color: #bdc3c7 #ecf0f1;
}

.suggested-questions-container::-webkit-scrollbar {
  height: 6px;
}

.suggested-questions-container::-webkit-scrollbar-track {
  background: #ecf0f1;
  border-radius: 3px;
}

.suggested-questions-container::-webkit-scrollbar-thumb {
  background: #bdc3c7;
  border-radius: 3px;
}

.suggestion-btn {
  background: #eef2f7;
  color: #4a5568;
  border: 1px solid #e2e8f0;
  padding: 0.5rem 1rem;
  border-radius: 16px;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 500;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.suggestion-btn:hover {
  background: #e2e8f0;
  border-color: #dce4f0;
  color: #2d3748;
}
/* ===============================
   Voice Speed Slider (GLOBAL)
   =============================== */
/* 헤더 정렬 */
.tts-rate-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

/* 현재 속도 배지 */
.current-rate-badge {
  background: #fff7ed;           /* 연한 오렌지 */
  color: #f97316;                /* 메인 오렌지 */
  font-size: 12px;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 999px;
  line-height: 1;
  box-shadow: inset 0 0 0 1px rgba(249, 115, 22, 0.35);
}


.tts-rate-wrapper {
  background: #ffffff;
  border-radius: 20px;
  padding: 16px 20px 18px;
  box-shadow: 0 6px 20px rgba(0,0,0,0.08);
}

.tts-rate-title {
  margin: 0 0 12px 0;
  font-size: 16px;
  font-weight: 600;
  color: #111827;
  display: flex;
  align-items: center;
  gap: 6px;
}

.tts-rate-container {
  width: 100%;
}

/* 회색 트랙 */
.rate-buttons-wrapper {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  align-items: center;
  background: #f3f4f6;
  border-radius: 999px;
  padding: 4px;
  height: 44px;
}

/* 버튼 기본 */
.rate-btn {
  border: none;
  background: transparent;
  height: 36px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  color: #9ca3af;
  cursor: pointer;
  transition: all 0.2s ease;
}

/* hover */
.rate-btn:hover {
  color: #111827;
}

/* 선택된 pill */
.rate-btn.active {
  background: #f97316; /* Figma 오렌지 */
  color: #ffffff;
  box-shadow: 0 4px 10px rgba(249, 115, 22, 0.35);
}



/* 반응형 */
/* 반응형 디자인 */
@media (max-width: 1200px) {
  .detail-content {
    grid-template-columns: 450px 1fr;
    gap: 1rem;
  }
}

@media (max-width: 1024px) {
  .detail-content {
    grid-template-columns: 1fr;
    height: auto;
    padding: 1rem;
  }
  
  .image-section {
    order: 1;
    margin-bottom: 1rem;
    height: auto; /* 높이 자동 조정 */
  }
  
  .analysis-section {
    order: 2;
    height: auto; /* 높이 자동 조정 */
  }
  
  .image-wrapper {
    max-height: 50vh; /* 화면 높이에 비례하여 조정 */
  }
  
  .timer-container {
    flex-direction: column;
    gap: 0.5rem;
  }
  
  .timer-section {
    min-width: auto;
  }
  
  .chat-panel {
    height: 60vh; /* 화면 높이에 비례하여 조정 */
  }
}

/* 새로운 ImageData 관련 스타일 */
.analysis-status {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
  align-items: center;
}

.analysis-status:first-child {
  border-bottom: 1px solid #e9ecef;
  padding-bottom: 1rem;
  margin-bottom: 1rem;
}

.status-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
  background: #f8f9fa;
  color: #6c757d;
}

.status-badge.uploaded {
  background: #d4edda;
  color: #155724;
}

.status-badge.analyzed {
  background: #e2e3f7;
  color: #383d41;
}

/* imagedata-info 스타일 제거됨 - imagedata-details로 통합 */

.info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.info-item {
  background: #ffffff;
  border-radius: 12px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.info-item .label {
  font-size: 11px;
  color: #6b7280;
  font-weight: 500;
  
}

.info-item .value {
  font-size: 13px;
  color: #111827;
  font-weight: 700;
}


.ai-analysis {
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-radius: 20px;              /* ⬅ 더 둥글게 */
  padding: 16px;                    /* ⬅ 바깥 여백 추가 */
  box-shadow: 0 6px 20px rgba(0,0,0,0.06);
}

.ai-analysis-body {
  position: relative;
}

.analysis-json-container {
  position: relative;
}

.json-toggle-btn {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  background: #4a5568;
  color: white;
  border: none;
  padding: 0.25rem 0.75rem;
  border-radius: 4px;
  font-size: 0.8rem;
  cursor: pointer;
  z-index: 1;
  transition: background 0.3s ease;
}

.json-toggle-btn:hover {
  background: #2d3748;
}

.analysis-json {
  background: #2d3748;
  color: #e2e8f0;
  padding: 1rem;
  padding-top: 2.5rem;
  border-radius: 8px;
  font-size: 0.8rem;
  overflow-x: auto;
  white-space: pre-wrap;
  max-height: 200px;
  overflow-y: auto;
  line-height: 1.4;
  transition: max-height 0.3s ease;
}

.analysis-json.expanded {
  max-height: 600px;
}

@media (max-width: 768px) {
  .detail-header {
    padding: 1rem;
  }
  
  .detail-content {
    padding: 0.75rem;
    gap: 0.75rem;
  }
  
  .analysis-panel,
  .chat-panel {
    padding: 1rem;
  }
  
  .analysis-status {
    flex-direction: column;
    align-items: stretch;
  }
  
  .status-btn {
    margin-right: 0;
    margin-bottom: 0.5rem;
  }
    

 /* Chat Card */
.chat-panel {
  display: flex;
  flex-direction: column;
  height: 100%;            /* ⭐ 중요 */
}

/* Header */
.chat-panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.chat-panel-header h3 {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
}

.stt-indicator-bar.listening {
  color: #dc2626;
  background: #fee2e2;
}


.input-container {
  display: flex;
  gap: 8px;
}
.chat-panel {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 16px;

  display: flex;
  flex-direction: column;
}

.chat-input {
  flex: 1;
  padding: 10px 14px;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  font-size: 14px;
}

.chat-input:focus {
  outline: none;
  border-color: #f97316;
}

.send-btn {
  padding: 10px 16px;
  border-radius: 10px;
  border: none;
  background: #f97316;
  color: white;
  font-weight: 600;
  cursor: pointer;
}

.send-btn:disabled {
  background: #d1d5db;
  cursor: not-allowed;
}

/* TTS Button */
.tts-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  margin-left: 6px;
}

  
  .suggested-questions {
    padding: 0.75rem;
    margin-top: 0.5rem;
  }
  
  .suggested-questions-container {
    gap: 0.3rem;
  }
  
  .suggestion-btn {
    padding: 0.5rem 0.8rem;
    font-size: 0.8rem;
  }
  
  .info-grid {
    grid-template-columns: 1fr;
  }
  
  .model-toggle-container {
    flex-direction: column;
    gap: 0.75rem;
    align-items: center;
  }
  
  .toggle-label {
    text-align: center;
  }
  
  .toggle-switch-wrapper {
    width: 100%;
    max-width: 250px;
  }
  
  /* 좌우배치 컨테이너 반응형 */
  .controls-horizontal-container {
    flex-direction: column;
    gap: 0.75rem;
    margin: 0.75rem 0;
    margin-bottom: 20px;   /* ← 이거 하나면 끝 */
  }
  
  /* 카드 */
.tts-rate-wrapper {
  background: #ffffff;
  border-radius: 20px;
  padding: 16px 20px 18px;
  box-shadow: 0 6px 20px rgba(0,0,0,0.08);
}

/* 타이틀 */
.tts-rate-title {
  margin: 0 0 12px 0;
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  display: flex;
  align-items: center;
  gap: 6px;
}

/* 트랙 */
.tts-rate-container {
  width: 100%;
}

.rate-buttons-wrapper {
  position: relative;
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  align-items: center;
  background: #f3f4f6;
  border-radius: 999px;
  padding: 4px;
  height: 44px;
}

/* 버튼 기본 */
.rate-btn {
  position: relative;
  z-index: 1;
  border: none;
  background: transparent;
  height: 36px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  color: #9ca3af;
  cursor: pointer;
  transition: color 0.2s ease;
}

/* hover */
.rate-btn:hover {
  color: #111827;
}

/* 선택된 pill */
.rate-btn.active {
  background: #f97316;         /* Figma 오렌지 */
  color: #ffffff;
  box-shadow:
    0 4px 10px rgba(249, 115, 22, 0.35);
}

}

@keyframes pulse-rate {
  from { 
    box-shadow: 0 6px 20px rgba(59, 130, 246, 0.4);
  }
  to { 
    box-shadow: 0 8px 25px rgba(59, 130, 246, 0.6);
  }
}


.ws-row { display:flex; gap:12px; flex-wrap:wrap; margin-bottom:12px; }
.ws-col { flex:1 1 320px; background:#f8f9fa; border:1px solid #e9ecef; border-radius:12px; padding:10px; }
.ws-title { margin:0 0 8px 0; font-size:.95rem; color:#2c3e50; }
.ws-frame { position:relative; background:white; border:1px solid #e3f2fd; border-radius:8px; min-height:180px; display:flex; align-items:center; justify-content:center; overflow:hidden; }
.ws-img { max-width:100%; max-height:320px; }
.ws-empty { color:#9aa0a6; font-size:.9rem; padding:20px; }
.ws-fps { position:absolute; top:8px; right:8px; font-size:.75rem; background:rgba(0,0,0,.7); color:#fff; padding:2px 6px; border-radius:6px; }
.ws-stats { margin-top:6px; display:flex; gap:8px; flex-wrap:wrap; font-size:.85rem; color:#4a5568; }

.header-actions {
  margin-left: auto;               /* 우측 정렬 */
  display: flex;
  align-items: center;
  gap: 8px;
}

.ws-indicator {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border-radius: 12px;
  background: #f5f7fb;
  border: 1px solid #e5e9f2;
  font-size: 12px;
  color: #475569;
}

.ws-indicator .dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #cbd5e1;            /* 기본: 회색(해제) */
  box-shadow: 0 0 0 2px rgba(0,0,0,0.04) inset;
}

.ws-indicator.on .dot {
  background: #22c55e;            /* 연결: 초록 */
}
.ws-video {
  width: 100%;
  max-height: 320px;
  object-fit: cover;
}


.connect-btn {
  font-size: 12px;
  padding: 6px 10px;
  border-radius: 8px;
  border: 1px solid #dce4f0;
  background: #ffffff;
  color: #334155;
  cursor: pointer;
  transition: background .2s, transform .05s;
}

.connect-btn:hover { background: #f8fafc; }
.connect-btn:active { transform: translateY(1px); }

</style>


